import { createChartingFilterAccessState, createChartingFilterOptions } from '@/features/charting-next/models/chartingFinderControls';
import { chartingFiltersToMarketQuery, marketQueryToChartingFilters } from '@/features/charting-next/models/chartingMarketQuery';
import { useChartingSession } from '@/features/charting-next/services/chartingSession';
import { computed, ref, watch } from '@vue/composition-api';

const cloneValue = (value) => JSON.parse(JSON.stringify(value ?? null));

export function useChartingFinderControls(workspace) {
    const sessionStore = useChartingSession();
    const filters = ref(null);
    const mutationError = ref('');
    const isApplying = ref(false);
    let syncingFromQuery = false;

    const definition = computed(() => workspace.finderDefinitionResource.data.value || null);
    const options = computed(() => createChartingFilterOptions(definition.value));
    const filterAccess = computed(() => createChartingFilterAccessState(sessionStore.features?.permissions || {}));
    const explorerState = computed(() => workspace.marketExplorer.state.value || {});
    const hasPendingChanges = computed(() => JSON.stringify(explorerState.value.draftQuery || {}) !== JSON.stringify(explorerState.value.appliedQuery || {}));
    const isReady = computed(() => Boolean(filters.value && explorerState.value.draftQuery));

    watch(
        () => explorerState.value.draftQuery,
        (query) => {
            if (!query) return;
            const nextFilters = marketQueryToChartingFilters(query);
            if (JSON.stringify(nextFilters) === JSON.stringify(filters.value)) return;
            syncingFromQuery = true;
            filters.value = cloneValue(nextFilters);
            syncingFromQuery = false;
            mutationError.value = '';
        },
        { immediate: true, deep: true }
    );

    watch(
        filters,
        (nextFilters) => {
            if (syncingFromQuery || !nextFilters || !explorerState.value.draftQuery) return;
            const nextQuery = chartingFiltersToMarketQuery(nextFilters, explorerState.value.draftQuery);
            if (JSON.stringify(nextQuery) !== JSON.stringify(explorerState.value.draftQuery)) workspace.marketExplorer.setDraft(nextQuery);
        },
        { deep: true }
    );

    const beginDraft = () => {
        mutationError.value = '';
        workspace.marketExplorer.beginDraft();
    };

    const cancelDraft = () => {
        mutationError.value = '';
        workspace.marketExplorer.cancelDraft();
    };

    const clearDraft = () => {
        mutationError.value = '';
        workspace.marketExplorer.clearDraft();
    };

    const applyFilters = async () => {
        if (!workspace.capabilities.value.canUseFilters || !filters.value) return null;
        isApplying.value = true;
        mutationError.value = '';

        try {
            return await workspace.marketExplorer.applyDraft();
        } catch (error) {
            mutationError.value = error.message || 'No se pudieron aplicar los filtros.';
            throw error;
        } finally {
            isApplying.value = false;
        }
    };

    return {
        filters,
        options,
        filterAccess,
        isReady,
        hasPendingChanges,
        isApplying,
        isResetting: computed(() => false),
        mutationError,
        beginDraft,
        cancelDraft,
        clearDraft,
        applyFilters,
        resetFilters: clearDraft
    };
}
